import { type ButtonConfig, SettingsType } from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import type { ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  dispatchAction,
  type ActionCreatorWithoutNamespace,
} from '@ardoq/rxbeach';
import { handleStoreSetting } from 'viewSettings/settingsHelper';

type GroupCollapseButtonArgs = {
  viewId: ViewIds;
  hasGroups: boolean;
  /** Applies the same change to the groups on screen; the view subscribes to it. */
  action: ActionCreatorWithoutNamespace;
  i18nLocale: I18nLocale;
};

/**
 * Collapse-all and expand-all, which write the setting rather than driving the canvas.
 *
 * `collapsedGroupIds` is the state: the graph is built with its groups already open or closed
 * (`isGroupCollapsed` in the shared builder), so writing the setting is the whole of the
 * operation, and a stored one is honoured on load without anything having to re-apply it. The
 * wildcard is the convention the yFiles views already store for "every group".
 */
export const collapseAllButton = ({
  viewId,
  hasGroups,
  action,
  i18nLocale,
}: GroupCollapseButtonArgs): ButtonConfig => ({
  id: 'collapseAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.collapseAll', 'Collapse all'),
  iconName: IconName.COLLAPSE_ALL,
  type: SettingsType.BUTTON,
  isDisabled: !hasGroups,
  onClick: () => {
    /* Both: the setting is what the graph is built from, and the action still arrives when the
       write is deduped or misrouted (see `flexibleBlockDiagram/actions.ts`). */
    handleStoreSetting(viewId, { collapsedGroupIds: [WILDCARD] });
    dispatchAction(action());
  },
});

export const expandAllButton = ({
  viewId,
  hasGroups,
  action,
  i18nLocale,
}: GroupCollapseButtonArgs): ButtonConfig => ({
  id: 'expandAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.expandAll', 'Expand all'),
  iconName: IconName.EXPAND_ALL,
  type: SettingsType.BUTTON,
  isDisabled: !hasGroups,
  onClick: () => {
    handleStoreSetting(viewId, { collapsedGroupIds: [] });
    dispatchAction(action());
  },
});
